import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
var _excluded = ["children"],
  _excluded2 = ["children"];
import React, { useState, useEffect, useRef } from 'react';
import { Dimensions, View, Text, TouchableOpacity, Platform } from 'react-native';
import Header from './Header';
import Row from './Row';
import Pagination from './Pagination';
import LengthMenu from './LengthMenu';
import { paginatedIndexes, pageCount } from '../functions/pagination';
import { formattedNumber } from '../functions/util';
import StringInput from './inputs/StringInput';
var Table = function Table(props) {
  var _styles$search;
  var entries = props.entries,
    columns = props.columns,
    order = props.order,
    updateEntries = props.updateEntries,
    page = props.page,
    count = props.count,
    lengthMenu = props.lengthMenu,
    pageLength = props.pageLength,
    paginationLinks = props.paginationLinks,
    searchTerm = props.searchTerm,
    styles = props.styles,
    movableColumns = props.movableColumns,
    component = props.component,
    componentProps = props.componentProps,
    buttons = props.buttons,
    isLoading = props.isLoading,
    htmlTable = props.htmlTable,
    disableSearch = props.disableSearch,
    alwaysSort = props.alwaysSort,
    multiSort = props.multiSort,
    refetch = props.refetch,
    isLoadingComponent = props.isLoadingComponent;
  var copyComponent = props.copyComponent;
  var tableRef = useRef(null);
  var currentColumnKeys = columns.map(function (column) {
    return column.key;
  });
  var _useState = useState(Dimensions.get('window')),
    _useState2 = _slicedToArray(_useState, 2),
    dimensions = _useState2[0],
    setDimensions = _useState2[1];
  var _useState3 = useState(_toConsumableArray(currentColumnKeys)),
    _useState4 = _slicedToArray(_useState3, 2),
    columnKeys = _useState4[0],
    setColumnKeys = _useState4[1];
  var _useState5 = useState(_toConsumableArray(currentColumnKeys)),
    _useState6 = _slicedToArray(_useState5, 2),
    columnOrder = _useState6[0],
    setColumnOrder = _useState6[1];
  var orderedColumns = columnOrder.map(function (key) {
    return columns.find(function (column) {
      return column.key === key;
    });
  });
  var updateDimensions = function updateDimensions() {
    setDimensions(Dimensions.get('window'));
  };
  useEffect(function () {
    return window.onresize = updateDimensions;
  }, []);

  // If columns have been added/removed, need to reset order, or else the
  // columns won't update.
  if (JSON.stringify(currentColumnKeys) !== JSON.stringify(columnKeys)) {
    setColumnKeys(_toConsumableArray(currentColumnKeys));
    setColumnOrder(_toConsumableArray(currentColumnKeys));
  }

  /**
   * @param {string} key - Column key.
   * @param {boolean} append - True to append to order.
   */
  var updateOrder = function updateOrder(key, append) {
    var _order$find;
    if ('prevent' === columns.find(function (column) {
      return column.key === key;
    }).sort) {
      return false;
    }
    var oldDirection = (_order$find = order.find(function (orderData) {
      return (orderData === null || orderData === void 0 ? void 0 : orderData.key) === key;
    })) === null || _order$find === void 0 ? void 0 : _order$find.direction;
    var newOrder;
    if (multiSort && append) {
      if ('asc' === oldDirection) {
        newOrder = _toConsumableArray(order);
        newOrder.find(function (orderData) {
          return (orderData === null || orderData === void 0 ? void 0 : orderData.key) === key;
        }).direction = 'desc';
      } else if ('desc' === oldDirection) {
        newOrder = order.filter(function (orderData) {
          return (orderData === null || orderData === void 0 ? void 0 : orderData.key) !== key;
        });
      } else {
        newOrder = [].concat(_toConsumableArray(order), [{
          key: key,
          direction: 'asc'
        }]);
      }
    } else {
      if ('asc' === oldDirection) {
        newOrder = [{
          key: key,
          direction: 'desc'
        }];
      } else if ('desc' === oldDirection) {
        newOrder = [];
      } else {
        newOrder = [{
          key: key,
          direction: 'asc'
        }];
      }
    }
    if (0 === newOrder.length && alwaysSort) {
      newOrder = [{
        key: key,
        direction: 'asc'
      }];
    }
    updateEntries({
      newOrder: newOrder
    });
  };
  var moveColumn = function moveColumn(dragIndex, dropIndex) {
    var element = columnOrder[dragIndex];
    columnOrder.splice(dragIndex, 1);
    columnOrder.splice(dropIndex, 0, element);
    setColumnOrder(_toConsumableArray(columnOrder));
  };
  var search = function search(newSearchTerm) {
    updateEntries({
      newSearchTerm: newSearchTerm
    });
  };
  var _paginatedIndexes = paginatedIndexes(count, page, pageLength),
    _paginatedIndexes2 = _slicedToArray(_paginatedIndexes, 2),
    pageStart = _paginatedIndexes2[0],
    pageEnd = _paginatedIndexes2[1];
  var paginationData = "".concat(formattedNumber(pageStart + 1), "-").concat(formattedNumber(pageEnd), " of ") + formattedNumber(count);
  var numberOfPages = pageCount(count, pageLength);
  var copyToClipboard = function copyToClipboard() {
    var range = document.createRange();
    var selection = window.getSelection();
    selection.removeAllRanges();
    range.selectNode(tableRef.current);
    selection.addRange(range);
    document.execCommand('copy');
  };
  if (component) {
    var Component = component;
    return /*#__PURE__*/React.createElement(Component, Object.assign({
      pageLength: pageLength,
      updateEntries: updateEntries,
      lengthMenu: lengthMenu,
      searchTerm: searchTerm,
      search: search,
      orderedColumns: orderedColumns,
      dimensions: dimensions,
      movableColumns: movableColumns,
      moveColumn: moveColumn,
      order: order,
      updateOrder: updateOrder,
      styles: styles,
      entries: entries,
      page: page,
      numberOfPages: numberOfPages,
      paginationData: paginationData,
      isLoading: isLoading,
      buttons: buttons
    }, componentProps));
  }
  var tableStyle = _objectSpread({
    marginTop: 8
  }, styles === null || styles === void 0 ? void 0 : styles.table);
  var searchStyle = _objectSpread({
    flexDirection: 'row',
    alignItems: 'baseline'
  }, styles === null || styles === void 0 ? void 0 : (_styles$search = styles.search) === null || _styles$search === void 0 ? void 0 : _styles$search.container);
  var functionRowStyle = _objectSpread({
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 5
  }, styles === null || styles === void 0 ? void 0 : styles.functionRow);
  var HtmlTableComponent = function HtmlTableComponent(_ref) {
    var children = _ref.children,
      props = _objectWithoutProperties(_ref, _excluded);
    return /*#__PURE__*/React.createElement("table", Object.assign({
      ref: tableRef
    }, props), children);
  };
  var HtmlTbodyComponent = function HtmlTbodyComponent(_ref2) {
    var children = _ref2.children,
      props = _objectWithoutProperties(_ref2, _excluded2);
    return /*#__PURE__*/React.createElement("tbody", props, children);
  };
  var showPagination = lengthMenu || pageLength ? true : undefined;
  var topPagination = showPagination;
  var bottomPagination = showPagination;
  if ('top' === paginationLinks) {
    bottomPagination = false;
  }
  if ('bottom' === paginationLinks) {
    topPagination = false;
  }
  var htmlTableAndWeb = htmlTable && 'web' === Platform.OS;
  var TableComponent = htmlTableAndWeb ? HtmlTableComponent : View;
  var TableBodyComponent = htmlTableAndWeb ? HtmlTbodyComponent : React.Fragment;
  if (htmlTableAndWeb) {
    if (!tableStyle.width) {
      tableStyle.width = '100%';
    }
  }
  var refetchComponent = typeof refetch === 'function' ? /*#__PURE__*/React.createElement(TouchableOpacity, {
    onPress: refetch
  }, /*#__PURE__*/React.createElement(View, {
    style: {
      paddingHorizontal: 6
    }
  }, /*#__PURE__*/React.createElement(Text, {
    style: {
      fontWeight: 'bold'
    }
  }, "\u21BB"))) : refetch;
  if (copyComponent) {
    copyComponent = /*#__PURE__*/React.createElement(TouchableOpacity, {
      onPress: copyToClipboard
    }, true === copyComponent ? /*#__PURE__*/React.createElement(View, {
      style: {
        paddingHorizontal: 6
      }
    }, /*#__PURE__*/React.createElement(Text, {
      style: {
        fontWeight: 'bold',
        fontSize: 20,
        marginTop: -5
      }
    }, "\u2398")) : copyComponent);
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(View, {
    style: functionRowStyle
  }, /*#__PURE__*/React.createElement(View, {
    style: {
      flexDirection: 'row'
    }
  }, /*#__PURE__*/React.createElement(Text, null, formattedNumber(count), " entries"), refetchComponent, htmlTableAndWeb && copyComponent), topPagination && /*#__PURE__*/React.createElement(Pagination, {
    page: page,
    numberOfPages: numberOfPages,
    onPageChange: function onPageChange(newPage) {
      return updateEntries({
        newPage: newPage
      });
    },
    styles: styles === null || styles === void 0 ? void 0 : styles.pagination
  })), /*#__PURE__*/React.createElement(View, {
    style: functionRowStyle
  }, lengthMenu && lengthMenu.length > 1 && /*#__PURE__*/React.createElement(LengthMenu, {
    pageLength: pageLength,
    updateEntries: updateEntries,
    options: lengthMenu
  }), isLoading && (isLoadingComponent || /*#__PURE__*/React.createElement(View, {
    style: {
      paddingHorizontal: 10
    }
  }, /*#__PURE__*/React.createElement(Text, {
    style: {
      fontWeight: 'bold',
      fontSize: 16
    }
  }, "Updating Data..."))), buttons, /*#__PURE__*/React.createElement(View, {
    style: searchStyle
  }, disableSearch ? null : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, null, "Search"), /*#__PURE__*/React.createElement(View, {
    style: {
      paddingHorizontal: 6
    }
  }, /*#__PURE__*/React.createElement(StringInput, {
    value: searchTerm || '',
    onChange: function onChange(value) {
      return search(value);
    }
  }))))), /*#__PURE__*/React.createElement(TableComponent, {
    style: tableStyle
  }, /*#__PURE__*/React.createElement(Header, {
    columns: orderedColumns,
    dimensions: dimensions,
    movableColumns: movableColumns,
    moveColumn: moveColumn,
    order: order,
    updateOrder: updateOrder,
    styles: styles === null || styles === void 0 ? void 0 : styles.header,
    htmlTable: htmlTableAndWeb
  }), /*#__PURE__*/React.createElement(TableBodyComponent, null, entries.map(function (entry, index) {
    return /*#__PURE__*/React.createElement(Row, {
      key: entry.id,
      entry: entry,
      columns: orderedColumns,
      index: index,
      dimensions: dimensions,
      styles: styles === null || styles === void 0 ? void 0 : styles.data,
      htmlTable: htmlTableAndWeb
    });
  }))), bottomPagination && /*#__PURE__*/React.createElement(Pagination, {
    page: page,
    numberOfPages: numberOfPages,
    onPageChange: function onPageChange(newPage) {
      return updateEntries({
        newPage: newPage
      });
    },
    label: paginationData,
    styles: styles === null || styles === void 0 ? void 0 : styles.pagination
  }));
};
export default Table;